import React, { memo, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PartnerAvatar from './PartnerAvatar';
import { DashboardJob, shortId } from './jobModel';
import { monoSx, pillButtonSx } from './styles';

interface ConfirmDeleteDialogProps {
  /** Jobs awaiting confirmation; null closes the dialog. */
  jobs: DashboardJob[] | null;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({ jobs, deleting, onCancel, onConfirm }) => {
  // Keep content stable while the dialog fades out.
  const lastJobs = useRef<DashboardJob[]>([]);
  if (jobs?.length) lastJobs.current = jobs;
  const shown = lastJobs.current;
  const many = shown.length > 1;
  const single = shown.length === 1 ? shown[0] : null;

  return (
    <Dialog
      open={Boolean(jobs?.length)}
      onClose={deleting ? undefined : onCancel}
      role="alertdialog"
      aria-labelledby="confirm-delete-title"
      PaperProps={{ sx: { width: '100%', maxWidth: 420, borderRadius: 5, p: 3, display: 'flex', flexDirection: 'column', gap: 2 } }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'error.main',
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
        }}
      >
        <DeleteOutline />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography id="confirm-delete-title" component="h2" sx={{ fontSize: 20, fontWeight: 600 }}>
          {many ? `Delete ${shown.length} jobs?` : 'Delete this job?'}
        </Typography>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5 }}>
          {many ? 'These jobs and their files' : 'This job and its files'} will be permanently removed. This can’t be undone.
        </Typography>
      </Box>
      {single ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 3, bgcolor: 'action.hover' }}>
          <PartnerAvatar name={single.partnerName} variant="inverse" />
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 500, fontSize: 14 }}>
              {single.partnerName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {single.typeLabel} ·{' '}
              <Box component="span" sx={monoSx}>
                {shortId(single.id)}
              </Box>
            </Typography>
          </Box>
        </Box>
      ) : null}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button onClick={onCancel} disabled={deleting} sx={pillButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutline />}
          // The theme forces contained buttons to the brand color; keep destructive red.
          sx={{ ...pillButtonSx, bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
        >
          {many ? `Delete ${shown.length} jobs` : 'Delete job'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default memo(ConfirmDeleteDialog);
